import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

/**
 * AtelierPage — Patrice' geheimer Materialien-Ordner.
 * Nicht in Navigation, nur via Direkt-Link erreichbar: /atelier
 * noindex/nofollow, eigene Brand-Optik, Passwort-Gate „Peony".
 */

const ATELIER_KEY = "sgb_atelier_unlocked_v1";
const PASSWORD = "Peony";

/* ============ Passwort-Gate ============ */
const PasswordGate = ({ onUnlock }) => {
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (value.trim().toLowerCase() === PASSWORD.toLowerCase()) {
      try { sessionStorage.setItem(ATELIER_KEY, "1"); } catch {}
      onUnlock();
    } else {
      setError(true);
      setValue("");
    }
  };

  return (
    <div className="min-h-screen bg-depth text-canvas flex items-center justify-center relative px-6">
      <div className="sgb-ambient" aria-hidden="true" />
      <form
        onSubmit={submit}
        data-testid="atelier-gate-form"
        className="relative max-w-md w-full text-center"
      >
        <p className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-signal">
          Privater Bereich
        </p>
        <h1 className="font-display mt-6 text-5xl md:text-6xl leading-[0.95] tracking-tight">
          Emmis <span className="italic-serif text-signal">Atelier</span>
        </h1>
        <p className="mt-6 font-light text-warmth/85 leading-relaxed">
          Sag das Kennwort, dann öffnet sich die Tür. 🌸
        </p>
        <input
          type="password"
          value={value}
          onChange={(e) => { setValue(e.target.value); setError(false); }}
          autoFocus
          data-testid="atelier-gate-input"
          aria-label="Kennwort"
          placeholder="Kennwort"
          className="mt-10 w-full bg-transparent border-b border-warmth/40 focus:border-signal focus:outline-none py-3 text-canvas font-light text-center text-lg tracking-[0.2em] placeholder:text-warmth/40 transition-colors"
        />
        {error && (
          <p data-testid="atelier-gate-error" className="mt-4 font-mono text-[0.62rem] tracking-[0.32em] uppercase text-signal">
            Nicht ganz. Probier noch einmal.
          </p>
        )}
        <button
          type="submit"
          data-testid="atelier-gate-submit"
          className="mt-8 inline-block px-8 py-3 border border-warmth/40 hover:bg-canvas hover:text-depth transition-colors font-mono text-[0.66rem] tracking-[0.32em] uppercase"
        >
          Eintreten ↗
        </button>
        <p className="mt-12 font-mono text-[0.55rem] tracking-[0.32em] uppercase text-warmth/55">
          noindex · nofollow · privat
        </p>
      </form>
    </div>
  );
};

/* ============ Section / Block / Tag Primitives ============ */
const Section = ({ id, eyebrow, title, status, children }) => (
  <section id={id} className="border-t border-warmth/15 pt-12 md:pt-16 pb-12 md:pb-16">
    <div className="flex items-baseline justify-between gap-4 flex-wrap">
      <p className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-signal">
        {eyebrow}
      </p>
      {status && (
        <span
          className="font-mono text-[0.55rem] tracking-[0.32em] uppercase px-2 py-0.5 border"
          style={{
            color: status === "ready" ? "#FF7100" : "#5C4752",
            borderColor: status === "ready" ? "#FF7100" : "#5C475255",
          }}
        >
          {status === "ready" ? "Bereit" : "In Planung"}
        </span>
      )}
    </div>
    <h2 className="font-display mt-4 text-3xl md:text-4xl leading-tight text-canvas">
      {title}
    </h2>
    <div className="mt-8 font-light text-warmth/90 text-base md:text-[1.05rem] leading-relaxed max-w-3xl space-y-5">
      {children}
    </div>
  </section>
);

const Block = ({ label, children }) => (
  <div className="mt-6 border border-warmth/20 bg-canvas/[0.025] p-5 md:p-6">
    {label && (
      <p className="font-mono text-[0.6rem] tracking-[0.32em] uppercase text-signal mb-3">
        {label}
      </p>
    )}
    <div className="text-warmth/90 whitespace-pre-line leading-relaxed text-[0.98rem]">
      {children}
    </div>
  </div>
);

const Tag = ({ children }) => (
  <span className="inline-block px-2.5 py-1 mr-1.5 mb-1.5 border border-warmth/25 text-warmth/85 font-mono text-[0.66rem] tracking-[0.18em] uppercase">
    {children}
  </span>
);

/* ============ Carousel-Slide (CLEAR SIGNAL™) ============ */
const Slide = ({ n, total, title, body, accent }) => (
  <div
    className="aspect-square flex flex-col justify-between p-6 md:p-8 relative overflow-hidden"
    style={{
      background: accent === "dark" ? "#28001b" : "#fdfdfd",
      color: accent === "dark" ? "#fdfdfd" : "#28001b",
      border: "1px solid rgba(82,51,69,0.2)",
    }}
  >
    <div className="flex items-start justify-between">
      <span className="font-mono text-[0.55rem] tracking-[0.32em] uppercase" style={{ color: "#ff7100" }}>
        Slide {n} / {total}
      </span>
      <span className="font-mono text-[0.55rem] tracking-[0.32em] uppercase opacity-70">
        Sichtbar · Gut Bewertet
      </span>
    </div>
    <div>
      <h3
        className="font-display text-2xl md:text-[1.85rem] leading-[1.05] tracking-tight"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {title}
      </h3>
      <p className="mt-4 text-[0.85rem] md:text-[0.95rem] leading-relaxed" style={{ opacity: 0.85 }}>
        {body}
      </p>
    </div>
    <div className="font-mono text-[0.55rem] tracking-[0.32em] uppercase" style={{ opacity: 0.6 }}>
      sichtbargutbewertet.de
    </div>
  </div>
);

/* ============ Reel-Mockup ============ */
const ReelFrame = ({ n, total, label, body, bg }) => (
  <div
    className="aspect-[9/16] flex flex-col justify-end p-5 relative overflow-hidden"
    style={{
      background: bg || "#28001b",
      color: "#fdfdfd",
      border: "1px solid rgba(255,255,255,0.08)",
    }}
  >
    <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
      <span className="font-mono text-[0.5rem] tracking-[0.3em] uppercase" style={{ color: "#ff7100" }}>
        {label}
      </span>
      <span className="font-mono text-[0.5rem] tracking-[0.3em] uppercase opacity-70">
        {n}/{total}
      </span>
    </div>
    <p
      className="font-display text-xl md:text-2xl leading-[1.1]"
      style={{ fontFamily: "'Cormorant Garamond', serif" }}
    >
      {body}
    </p>
    <p className="mt-3 font-mono text-[0.5rem] tracking-[0.3em] uppercase opacity-60">
      sichtbargutbewertet.de
    </p>
  </div>
);

/* ============ Inhaltsseite (nach Unlock) ============ */
const AtelierContent = () => {
  const today = new Date().toLocaleDateString("de-DE", {
    day: "2-digit", month: "long", year: "numeric",
  });

  // CLEAR SIGNAL Buchstaben (für Carousel + LinkedIn-Serie)
  const letters = [
    { l: "C", word: "Clarity",   body: "Worüber sprichst du wirklich — und was bleibt im Kopf, wenn man weiterscrollt?" },
    { l: "L", word: "Language",  body: "Klingst du wie du selbst — oder wie ein KI-Generator von 2023?" },
    { l: "E", word: "Evidence",  body: "Was beweist, dass du kannst, wofür du wirbst? Echte Arbeit oder leere Versprechen?" },
    { l: "A", word: "Aesthetic", body: "Wirkt deine Optik absichtlich — oder zufällig zusammengeschoben?" },
    { l: "R", word: "Resonance", body: "Triffst du die, die du meinst — oder reden alle, nur nicht die Richtigen?" },
    { l: "S", word: "Story",     body: "Gibt es einen roten Faden — oder springt jeder Post in eine andere Welt?" },
    { l: "I", word: "Integrity", body: "Stimmt das Innen mit dem Außen überein? Tust du, was du sagst?" },
    { l: "G", word: "Growth",    body: "Ist da Bewegung — oder Stillstand seit Monaten?" },
    { l: "N", word: "Network",   body: "Wer reagiert? Wer fehlt? Welche Tür ist noch zu?" },
    { l: "A", word: "Authority", body: "Glaubt man dir den Anspruch, den du erhebst?" },
    { l: "L", word: "Legacy",    body: "Was bleibt, wenn man weiterscrollt? Eindruck — oder Leere?" },
  ];
  const totalSlides = letters.length + 2; // Cover + 11 Buchstaben + CTA

  return (
    <div className="min-h-screen bg-depth text-canvas relative">
      <div className="sgb-ambient" aria-hidden="true" />

      {/* ============ Header ============ */}
      <header className="relative px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-12 md:pb-16 border-b border-warmth/20">
        <div className="max-w-5xl">
          <p className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-warmth/70">
            Privat · Nur via Direktlink · Stand {today}
          </p>
          <h1 className="font-display mt-6 text-5xl md:text-7xl leading-[0.95] tracking-tight">
            Emmis <span className="italic-serif text-signal">Atelier</span>
          </h1>
          <p className="mt-8 font-light text-warmth/90 text-lg md:text-xl leading-relaxed max-w-3xl">
            Dein Gratis-Buffet 🌸 — alle Marketing-Materialien für{" "}
            <em>sichtbargutbewertet.de</em> an einem Ort. Du bekommst diesen einen Link.
            Niemand sonst.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/" data-testid="atelier-back-home"
              className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-warmth/80 hover:text-canvas transition-colors border border-warmth/30 px-4 py-2">
              ← Zurück zu sichtbargutbewertet
            </Link>
            <a href="#index"
              className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-warmth/80 hover:text-canvas transition-colors border border-warmth/30 px-4 py-2">
              Inhalts-Verzeichnis ↓
            </a>
          </div>
        </div>
      </header>

      {/* ============ Inhaltsverzeichnis ============ */}
      <section id="index" className="relative px-6 md:px-12 lg:px-16 py-12 md:py-16 border-b border-warmth/15">
        <p className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-signal">Inhalt</p>
        <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-8 max-w-4xl font-light text-warmth/90">
          <li><a href="#linkedin" className="hover:text-canvas transition-colors">01 — LinkedIn-Texte (Headline · About · Experience)</a></li>
          <li><a href="#keywords" className="hover:text-canvas transition-colors">02 — Keyword- & Hashtag-Listen (DACH)</a></li>
          <li><a href="#posts" className="hover:text-canvas transition-colors">03 — Deine ersten 3 Posts</a></li>
          <li><a href="#carousels" className="hover:text-canvas transition-colors">04 — Carousel CLEAR SIGNAL™ (13 Slides)</a></li>
          <li><a href="#reels" className="hover:text-canvas transition-colors">05 — Reels-Mockups (3 Stück)</a></li>
          <li><a href="#linkedin-series" className="hover:text-canvas transition-colors">06 — LinkedIn-Serie · 1 Buchstabe pro Woche</a></li>
          <li><a href="#briefings" className="hover:text-canvas transition-colors">07 — Briefings &amp; Notizen</a></li>
        </ul>
      </section>

      <main className="relative px-6 md:px-12 lg:px-16 pb-24 md:pb-32 max-w-5xl">

        {/* ----- 01 · LinkedIn ----- */}
        <Section id="linkedin" eyebrow="01 · LinkedIn" title="LinkedIn-Texte für Sichtbar Gut Bewertet" status="ready">
          <p>
            Drei Headline-Varianten, ein About-Text in deinem Ton, Skills und eine Experience-Beschreibung.
            Copy-Paste-fähig. Kein „Ich lösche Bewertungen"-Wording — alles diskret und strukturiert formuliert.
          </p>

          <Block label="Headline · Variante A (kurz · klar)">
{`Reputationsmanagement & Bewertungs­management im DACH-Raum · Sichtbar Gut Bewertet`}
          </Block>

          <Block label="Headline · Variante B (Story)">
{`Ich helfe Marken, wieder fair wahrgenommen zu werden. Bewertungen prüfen · Profile schärfen · Webseiten bauen.`}
          </Block>

          <Block label="Headline · Variante C (Service-Stack)">
{`Bewertungsmanagement · CLEAR SIGNAL™ Profilanalyse · Account-Wiederherstellung · Webdesign · DACH`}
          </Block>

          <Block label="About · Langtext">
{`Ich bin Patrice Schreiter — Brand- und Reputations-Strategin aus Sachsen, arbeite für Kund:innen in ganz Deutschland, Österreich und der Schweiz.

Unter „Sichtbar Gut Bewertet" prüfe und strukturiere ich, was online über dich sichtbar ist:

— unfaire, falsche oder regelwidrige Bewertungen auf Google und Trustpilot fachgerecht zur Prüfung einreichen
— Social-Media-Profile mit der CLEAR SIGNAL™-Methode analysieren und schärfen
— gesperrte Instagram- und TikTok-Accounts strukturiert wiederherstellen helfen
— eigenständige, ästhetische Webseiten bauen — keine Templates, keine Floskeln

Was ich nicht verspreche: dass eine Plattform meine Einreichung entscheidet. Was ich verspreche: eine saubere, nachvollziehbare Bearbeitung. Diskret. Strukturiert. Ohne Drama.

Wenn du dich gerade fragst, warum deine Sichtbarkeit nicht das spiegelt, wofür du wirklich stehst — schreib mir.

sichtbargutbewertet.de · kontakt@sichtbargutbewertet.de`}
          </Block>

          <Block label="Skills (für LinkedIn-Skill-Liste)">
{`Reputationsmanagement · Bewertungsmanagement · Online Reputation · Krisenkommunikation · Social Media Audit · Profilanalyse · Account-Wiederherstellung · DSA-Beschwerden · Webdesign · UX/UI · Brand Storytelling · Markenstrategie · Sichtbarkeitsstrategie · Content-Strategie · DACH-Marketing`}
          </Block>

          <Block label={`Experience-Eintrag · „Sichtbar Gut Bewertet"`}>
{`Sichtbar Gut Bewertet — Inhaberin & Strategin
2026 – heute · Limbach-Oberfrohna · arbeitet remote in ganz DACH

Spezialistin für Reputations- und Bewertungsmanagement (Google, Trustpilot), Social-Media-Profilanalyse (CLEAR SIGNAL™-Methode), Account-Wiederherstellung (Instagram, TikTok) und Webdesign für Selbständige, Studios und kleine Marken im DACH-Raum.

Schwerpunkte:
· Prüfung und fachgerechte Einreichung regelwidriger Bewertungen
· CLEAR SIGNAL™ Profilanalyse — strukturierter Audit, 11 Achsen
· Begleitung bei DSA-Beschwerden & Account-Sperrungen
· Webseiten-Konzeption & -Umsetzung, eigen statt Template-ware`}
          </Block>
        </Section>

        {/* ----- 02 · Keywords ----- */}
        <Section id="keywords" eyebrow="02 · Sichtbarkeit" title="Keyword- & Hashtag-Listen" status="ready">
          <p>
            Suchbegriffe deiner echten Kund:innen — nach Themenfeldern sortiert.
            Für SEO bereits im <code>index.html</code> hinterlegt, hier zum Mitnehmen für LinkedIn,
            Instagram-Captions und Anzeigen.
          </p>

          <Block label="SEO-Keywords · Bewertungen (DACH)">
{`Google Bewertung löschen · negative Bewertung löschen lassen · schlechte Google Bewertung löschen · falsche Bewertung löschen · Trustpilot Bewertung löschen · Google Rezension löschen · gefälschte Bewertung melden · Fake Bewertung entfernen · 1 Stern Bewertung löschen · Bewertung löschen lassen · Bewertungen löschen · Bewertungsmanagement · Reputationsmanagement · Online-Reputation verbessern`}
          </Block>

          <Block label="SEO-Keywords · Accounts (DACH)">
{`Instagram Account gesperrt · TikTok Account gesperrt · Account gesperrt · Account entsperren · gesperrter Account wiederherstellen · Account Wiederherstellung · DSA Beschwerde · Social Media Audit · Instagram Profil optimieren · Social Media Account Analyse`}
          </Block>

          <Block label="SEO-Keywords · Webdesign (DACH)">
{`Webseite erstellen · Webseite bauen lassen · Webseiten bauen · Webseiten gestalten · Webdesign · Webdesign DACH · Webdesign Sachsen · Homepage · Homepage erstellen`}
          </Block>

          <Block label="Hashtags · Instagram (Mix Reichweite + Nische)">
            <div className="flex flex-wrap">
              <Tag>#Reputationsmanagement</Tag><Tag>#Bewertungsmanagement</Tag><Tag>#GoogleBewertung</Tag>
              <Tag>#Trustpilot</Tag><Tag>#OnlineReputation</Tag><Tag>#SocialMediaAudit</Tag>
              <Tag>#InstagramTipps</Tag><Tag>#AccountGesperrt</Tag><Tag>#TikTokGesperrt</Tag>
              <Tag>#WebdesignDACH</Tag><Tag>#WebsiteErstellen</Tag><Tag>#HomepageErstellen</Tag>
              <Tag>#PatriceStudio</Tag><Tag>#SichtbarGutBewertet</Tag><Tag>#CLEARSIGNAL</Tag>
              <Tag>#MarkeMitHaltung</Tag><Tag>#SichtbarkeitMitSubstanz</Tag><Tag>#DigitalesAtelier</Tag>
              <Tag>#Sachsen</Tag><Tag>#DACH</Tag>
            </div>
          </Block>

          <Block label="Hashtags · LinkedIn (5–7 reichen)">
            <div className="flex flex-wrap">
              <Tag>#Reputationsmanagement</Tag><Tag>#Bewertungsmanagement</Tag>
              <Tag>#OnlineReputation</Tag><Tag>#Selbständigkeit</Tag>
              <Tag>#DACH</Tag><Tag>#Webdesign</Tag><Tag>#SichtbarGutBewertet</Tag>
            </div>
          </Block>

          <Block label="Hashtags · TikTok (für Account-Reels)">
            <div className="flex flex-wrap">
              <Tag>#instagramgesperrt</Tag><Tag>#tiktokgesperrt</Tag><Tag>#accountgesperrt</Tag>
              <Tag>#accountwiederherstellen</Tag><Tag>#dsa</Tag><Tag>#selbstständig</Tag>
              <Tag>#smallbusinessdach</Tag><Tag>#bewertungen</Tag>
            </div>
          </Block>
        </Section>

        {/* ----- 03 · Erste Posts ----- */}
        <Section id="posts" eyebrow="03 · Storytelling" title="Deine ersten 3 Posts" status="ready">
          <p>
            Story-Format <em>„Wer bin ich → Was tue ich → Was biete ich an"</em>. Lang genug für LinkedIn,
            kürzbar für Instagram-Caption.
          </p>

          <Block label="Post 1/3 · Wer bin ich · Vorstellung">
{`Vielleicht hast du mich schon gesehen — als patrice.studio, als die mit den Pfingstrosen, als die, die „Webseiten baut".

Heute mache ich eine zweite Tür auf.

Sie heißt: Sichtbar Gut Bewertet.

Weil viele von euch nicht wegen einer Webseite kommen.
Sondern weil online etwas steht, das nicht stimmt.
Eine unfaire Bewertung. Ein gesperrter Account. Ein Profil, das aussieht wie tausend andere.

Sichtbar Gut Bewertet ist für genau diese Momente.
Strukturiert. Diskret. Nachvollziehbar.

Kein Versprechen, dass Google entscheidet, wie ich es will.
Aber das Versprechen, dass ich es sauber, fachlich und mit Haltung mache.

— Patrice
sichtbargutbewertet.de`}
          </Block>

          <Block label="Post 2/3 · Was tue ich · CLEAR SIGNAL™">
{`Manche Profile sehen aus wie Schaufenster im Regen.
Alles da. Aber niemand bleibt stehen.

Ich habe für genau diese Profile eine Methode entwickelt: CLEAR SIGNAL™.

11 Achsen. Eine Stunde Arbeit. Ein klarer Bericht.

C — Clarity: Worüber sprichst du wirklich?
L — Language: Klingst du wie du, oder wie alle?
E — Evidence: Was beweist, dass du kannst, was du sagst?
A — Aesthetic: Wirkt es absichtlich oder zufällig?
R — Resonance: Trifft es die, die du meinst?
S — Story: Gibt es einen roten Faden?
I — Integrity: Stimmt das Innen mit dem Außen?
G — Growth: Ist da Bewegung — oder Stillstand?
N — Network: Wer reagiert? Wer fehlt?
A — Authority: Glaubt man dir den Anspruch?
L — Legacy: Was bleibt, wenn man weiterscrollt?

Wenn du wissen willst, was du wirklich ausstrahlst — schreib mir.

sichtbargutbewertet.de · CLEAR SIGNAL™`}
          </Block>

          <Block label="Post 3/3 · Was biete ich an · Service-Stack">
{`Vier Dinge tue ich. Mehr nicht. Dafür diese gut.

01 — Bewertungsmanagement
Google- und Trustpilot-Bewertungen prüfen, einreichen lassen, sauber dokumentieren. Plus: aufbauen, was wirklich zu dir gehört.

02 — CLEAR SIGNAL™ Profilanalyse
Social-Media-Audit auf 11 Achsen. Ein Bericht, der dir zeigt, was du ausstrahlst — und was nicht.

03 — Account-Wiederherstellung
Instagram. TikTok. Strukturierte Begleitung bei Sperrungen, DSA-Beschwerden, Wiederherstellungsprozessen.

04 — Webdesign
Eine eigene Webseite. Keine Templates, keine Plattform-Schminke. Etwas, das wie du klingt.

Erstgespräch kostet nichts. Der Rest ist ehrlich kalkuliert.

sichtbargutbewertet.de`}
          </Block>
        </Section>

        {/* ----- 04 · Carousel CLEAR SIGNAL™ ----- */}
        <Section id="carousels" eyebrow="04 · Social Slides" title="Carousel · CLEAR SIGNAL™ (13 Slides)" status="ready">
          <p>
            Vollständiges 13-Slide-Carousel — quadratisch (1:1). Cover · 11 Buchstaben · CTA.
            Mit Screenshot oder Browser-„Bildschirmfoto" als PNG sichern. Funktioniert für Instagram,
            LinkedIn und Threads.
          </p>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Slide
              n="1" total={totalSlides} accent="dark"
              title={<>CLEAR <em className="italic-serif" style={{color:"#ff7100"}}>SIGNAL</em>™</>}
              body="Eine Methode, um zu prüfen, was dein Profil wirklich ausstrahlt — auf 11 Achsen."
            />
            {letters.map((it, idx) => (
              <Slide
                key={idx}
                n={String(idx + 2)}
                total={totalSlides}
                accent="light"
                title={<><span style={{color:"#ff7100"}}>{it.l}</span> · {it.word}</>}
                body={it.body}
              />
            ))}
            <Slide
              n={String(totalSlides)} total={totalSlides} accent="dark"
              title={<>Sichtbar.<br/><span className="italic-serif" style={{color:"#ff7100"}}>Gut Bewertet.</span></>}
              body="Erstgespräch kostenlos · sichtbargutbewertet.de"
            />
          </div>
        </Section>

        {/* ----- 05 · Reels ----- */}
        <Section id="reels" eyebrow="05 · Bewegtbild" title="Reels-Mockups (3 Stück · 9:16)" status="ready">
          <p>
            Drei 5-Frame-Mockups: <em>Bewertungsmanagement</em>, <em>Webdesign</em> und{" "}
            <em>Account gesperrt</em>. Bildschirm-Aufnahme abspielen → fertig fürs Reel.
          </p>

          {/* Reel 1: Bewertungsmanagement */}
          <p className="mt-10 font-mono text-[0.62rem] tracking-[0.32em] uppercase text-signal">
            Reel 01 · Bewertungsmanagement
          </p>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <ReelFrame n="1" total="5" label="HOOK" bg="#28001b"
              body={<>Eine schlechte Bewertung kann <em className="italic-serif" style={{color:"#ff7100"}}>alles</em> ins Wanken bringen.</>}/>
            <ReelFrame n="2" total="5" label="PROBLEM" bg="#523345"
              body={<>Sie ist falsch. Sie ist unfair. Sie ist von jemandem, der nie da war.</>}/>
            <ReelFrame n="3" total="5" label="LÖSUNG" bg="#28001b"
              body={<>Ich prüfe sie. Strukturiert. Diskret. Reiche sie fachgerecht ein.</>}/>
            <ReelFrame n="4" total="5" label="GRENZE" bg="#523345"
              body={<>Was Google entscheidet, entscheidet Google. Was ich tue, tue ich sauber.</>}/>
            <ReelFrame n="5" total="5" label="CTA" bg="#28001b"
              body={<>sichtbargutbewertet.de<br/><span className="italic-serif" style={{color:"#ff7100"}}>Erstgespräch kostenfrei.</span></>}/>
          </div>

          <Block label="Reel 01 · Sprech-Skript · 22 Sekunden">
{`(0–4s · Hook)  Eine schlechte Bewertung kann alles ins Wanken bringen.
(4–9s · Problem)  Sie ist falsch. Sie ist unfair. Oder sie ist von jemandem, der nie bei dir war.
(9–14s · Lösung)  Ich prüfe sie. Strukturiert. Diskret. Und reiche sie fachgerecht ein.
(14–18s · Grenze)  Was die Plattform entscheidet, entscheidet die Plattform. Was ich mache, mache ich sauber.
(18–22s · CTA)  Sichtbar gut bewertet. Erstgespräch kostenfrei.`}
          </Block>

          <Block label="Reel 01 · Caption (Instagram · TikTok)">
{`Eine einzige falsche Bewertung reicht — und der Mut, deine Webseite zu öffnen, ist weg.

Ich helfe nicht mit Tricks. Ich helfe mit Struktur.
Bewertungen prüfen, fachlich einreichen, sauber dokumentieren.

Was die Plattform am Ende entscheidet, bleibt bei der Plattform.
Aber dazwischen passiert echte Arbeit.

→ sichtbargutbewertet.de
#bewertungsmanagement #onlinereputation #googlebewertung #trustpilot #dach`}
          </Block>

          {/* Reel 2: Webdesign */}
          <p className="mt-12 font-mono text-[0.62rem] tracking-[0.32em] uppercase text-signal">
            Reel 02 · Webdesign
          </p>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <ReelFrame n="1" total="5" label="HOOK" bg="#28001b"
              body={<>Deine Webseite sieht aus wie <em className="italic-serif" style={{color:"#ff7100"}}>tausend andere</em>.</>}/>
            <ReelFrame n="2" total="5" label="PROBLEM" bg="#523345"
              body={<>Templates lassen sich nicht abschalten. Den Geschmack auch nicht.</>}/>
            <ReelFrame n="3" total="5" label="ANSATZ" bg="#28001b"
              body={<>Ich baue keine Templates. Ich baue, wie du klingst.</>}/>
            <ReelFrame n="4" total="5" label="VERSPRECHEN" bg="#523345"
              body={<>Eigene Typo. Eigene Farben. Eigene Sätze. Eine eigene Spur.</>}/>
            <ReelFrame n="5" total="5" label="CTA" bg="#28001b"
              body={<>sichtbargutbewertet.de<br/><span className="italic-serif" style={{color:"#ff7100"}}>Webdesign mit Substanz.</span></>}/>
          </div>

          <Block label="Reel 02 · Sprech-Skript · 22 Sekunden">
{`(0–4s · Hook)  Deine Webseite sieht aus wie tausend andere?
(4–9s · Problem)  Templates lassen sich abschalten. Den Geschmack der Vorlage nicht.
(9–14s · Ansatz)  Ich baue keine Templates. Ich baue, wie du klingst.
(14–18s · Versprechen)  Eigene Typo. Eigene Farben. Eigene Sätze. Eine eigene Spur.
(18–22s · CTA)  Sichtbar gut bewertet. Webdesign mit Substanz.`}
          </Block>

          <Block label="Reel 02 · Caption">
{`Templates sind bequem. Aber sie sind auch der Grund, warum 90 % aller Webseiten austauschbar wirken.

Wer eigen klingt, braucht eigene Räume.
Eigene Typografie. Eigene Farben. Eigene Architektur.

Ich baue Webseiten, die nach dir aussehen — nicht nach Wordpress-Theme „Lavender Bloom v3".

→ sichtbargutbewertet.de
#webdesigndach #websiteerstellen #webdesignsachsen #selbstständig #markenmitsubstanz`}
          </Block>

          {/* Reel 3: Account gesperrt */}
          <p className="mt-12 font-mono text-[0.62rem] tracking-[0.32em] uppercase text-signal">
            Reel 03 · Account gesperrt
          </p>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <ReelFrame n="1" total="5" label="HOOK" bg="#28001b"
              body={<>Account gesperrt — und mit ihm <em className="italic-serif" style={{color:"#ff7100"}}>Jahre Arbeit</em>.</>}/>
            <ReelFrame n="2" total="5" label="REALITY" bg="#523345"
              body={<>Kein Login. Keine Erklärung. Kein Mensch am Support-Bildschirm.</>}/>
            <ReelFrame n="3" total="5" label="ERSTE STUNDEN" bg="#28001b"
              body={<>Nicht 50 Mal anschreiben. Strukturieren. Dokumentieren. Belegen.</>}/>
            <ReelFrame n="4" total="5" label="WEG" bg="#523345"
              body={<>DSA-Beschwerde. Wiederherstellungsantrag. Saubere Linie statt Panik-Mails.</>}/>
            <ReelFrame n="5" total="5" label="CTA" bg="#28001b"
              body={<>sichtbargutbewertet.de<br/><span className="italic-serif" style={{color:"#ff7100"}}>Hilfe in den ersten 24 Stunden.</span></>}/>
          </div>

          <Block label="Reel 03 · Sprech-Skript · 22 Sekunden">
{`(0–4s · Hook)  Dein Account ist gesperrt. Und mit ihm Jahre Arbeit.
(4–9s · Reality)  Kein Login. Keine Erklärung. Kein Mensch am Support.
(9–14s · Erste Stunden)  Nicht 50 Mal anschreiben. Erst strukturieren. Dokumentieren. Belegen.
(14–18s · Weg)  DSA-Beschwerde. Wiederherstellungsantrag. Eine saubere Linie statt Panik-Mails.
(18–22s · CTA)  Sichtbar gut bewertet. Hilfe in den ersten 24 Stunden.`}
          </Block>

          <Block label="Reel 03 · Caption">
{`Wenn dein Instagram- oder TikTok-Account gesperrt ist, kippt fast alles gleichzeitig: Sichtbarkeit, Vertrauen, Umsatz, Selbstvertrauen.

Was du in den ersten 24 Stunden tust, entscheidet darüber, wie schnell — und ob — du wieder Zugriff bekommst.

Nicht 50 Mal anschreiben. Erst strukturieren. Dann einreichen.

Ich begleite dich durch DSA-Beschwerden, Wiederherstellungsanträge und alles, was zwischen Sperrung und Login passiert.

→ sichtbargutbewertet.de
#accountgesperrt #instagramgesperrt #tiktokgesperrt #dsa #socialmediadach`}
          </Block>
        </Section>

        {/* ----- 06 · LinkedIn-Serie ----- */}
        <Section id="linkedin-series" eyebrow="06 · Serie" title="LinkedIn-Serie · 1 Buchstabe pro Woche" status="ready">
          <p>
            11 Wochen, 11 Buchstaben — jede Woche ein eigener Post. Format: Hook → 2 Sätze Erklärung →
            eine Mini-Frage als Engagement-Trigger. Hashtags am Ende.
          </p>

          {letters.map((it, idx) => {
            const num = String(idx + 1).padStart(2, "0");
            // Buchstaben-spezifische Posts:
            const posts = [
              // C - Clarity
              `Clarity heißt nicht: alles wegkürzen.\nClarity heißt: das eine sagen, das übrig bleibt, wenn alles andere weg ist.\n\nFrage an dich: Wenn jemand dein Profil 4 Sekunden sieht — was bleibt im Kopf?`,
              // L - Language
              `Wer wie alle klingt, klingt nach niemandem.\n„Wir lieben Qualität und unsere Kunden stehen im Mittelpunkt" — kennt jeder. Vergisst jeder.\n\nFrage an dich: Welcher Satz auf deinem Profil ist so, dass nur du ihn schreiben würdest?`,
              // E - Evidence
              `Evidence ist das stillste Marketing.\nKein Versprechen. Keine Adjektive. Nur: hier ist die Arbeit.\n\nFrage an dich: Welchen Beweis hat jemand, dass du kannst, wofür du wirbst — bevor er „Termin buchen" klickt?`,
              // A - Aesthetic
              `Ästhetik ist nicht Dekoration. Ästhetik ist Haltung in sichtbar.\nWer absichtlich aussieht, sagt: hier wurde nachgedacht.\n\nFrage an dich: Sieht deine Marke aus, als hätte jemand sie entschieden — oder als hätte sie sich zufällig gefunden?`,
              // R - Resonance
              `Reichweite ist die Anzahl der Menschen, die dich sehen.\nResonance ist die Anzahl der Menschen, die sich angesprochen fühlen.\n\nFrage an dich: Wer sind die 100 Menschen, die du wirklich erreichen willst — und wissen die das?`,
              // S - Story
              `Eine Marke ohne Story ist eine Visitenkarte mit Logo.\nStory heißt nicht: dein Leben in 3 Akten. Story heißt: roter Faden zwischen deinen Posts.\n\nFrage an dich: Wenn jemand deine letzten 9 Posts liest — gibt es einen Faden, oder 9 Welten?`,
              // I - Integrity
              `Integrity ist langweilig im Marketing — und der teuerste Vermögenswert deiner Marke.\nSie kostet nichts. Bis sie weg ist.\n\nFrage an dich: Tust du an einem mittelmäßigen Dienstag noch das, was du an einem guten Montag versprochen hast?`,
              // G - Growth
              `Growth ist nicht Wachstum um des Wachstums willen.\nGrowth ist: kein Stillstand seit Monaten. Eine Bewegung. Eine Lernkurve. Etwas Neues seit März.\n\nFrage an dich: Was kannst du heute, das du vor sechs Monaten noch nicht konntest?`,
              // N - Network
              `Network sind nicht 5.000 Follower.\nNetwork sind die 12 Menschen, die einen Anruf annehmen, wenn du sie brauchst.\n\nFrage an dich: Wer fehlt in deinem 12er-Kreis — und schreibst du der Person diese Woche?`,
              // A - Authority
              `Autorität bekommst du nicht, weil du sie willst.\nAutorität bekommst du, weil andere sie dir geben.\n\nFrage an dich: Wer in deinem Feld zitiert dich, empfiehlt dich, schickt Kund:innen — und warum gerade die?`,
              // L - Legacy
              `Legacy klingt groß. Ist es nicht.\nLegacy heißt: was bleibt, wenn jemand deine Seite verlässt? Eine Idee? Ein Satz? Nichts?\n\nFrage an dich: Welcher eine Satz von dir hätte den Verdienst, hängen zu bleiben?`,
            ];
            return (
              <Block key={idx} label={`Woche ${num} · ${it.l} · ${it.word}`}>
                {posts[idx]}
                {`\n\n#CLEARSIGNAL #SichtbarGutBewertet #${it.word} #Reputationsmanagement #SichtbarkeitMitSubstanz`}
              </Block>
            );
          })}
        </Section>

        {/* ----- 07 · Briefings ----- */}
        <Section id="briefings" eyebrow="07 · Working Files" title="Briefings, Skripte & Notizen" status="open">
          <p>
            Platz für alles, was nicht in die anderen Kategorien passt — Sprach-Skripte,
            To-do-Listen für Shootings, Sammlungen für die nächste Iteration. Sag mir, was du brauchst,
            ich pflege es hier ein.
          </p>
          <p className="italic-serif text-warmth/80">
            Mögliche nächste Schritte:<br/>
            · Carousel „Was Bewertungs­management wirklich kann (und was nicht)"<br/>
            · Carousel „Webdesign — der Unterschied zwischen Template und Atelier"<br/>
            · Reel „CLEAR SIGNAL™ in 30 Sekunden erklärt"<br/>
            · LinkedIn-Karussell-Posts als statisches PNG-Set für Direkt-Upload
          </p>
        </Section>

        <div className="mt-20 pt-10 border-t border-warmth/15 text-warmth/70 font-mono text-[0.62rem] tracking-[0.32em] uppercase">
          Privater Bereich · noindex · Bookmark: /atelier · Kennwort: Peony
        </div>
      </main>
    </div>
  );
};

/* ============ Wrapper mit Gate ============ */
const AtelierPage = () => {
  const [unlocked, setUnlocked] = useState(() => {
    try { return sessionStorage.getItem(ATELIER_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Atelier · Patrice";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.title = prevTitle;
      document.head.removeChild(meta);
    };
  }, []);

  if (!unlocked) return <PasswordGate onUnlock={() => setUnlocked(true)} />;
  return <AtelierContent />;
};

export default AtelierPage;
